import {
  AuthError,
  UserCredential,
  getAdditionalUserInfo,
  sendEmailVerification,
} from "firebase/auth";
import {
  useSignInWithGithub,
  useSignInWithGoogle,
} from "react-firebase-hooks/auth";

import {
  captureLegacyEvent,
  captureProductEvent,
  markPendingEmailVerification,
  productAnalyticsEvents,
} from "../analytics/events";
import { firebaseAuth } from "../configs/firebase";
import { getBaseUrl } from "../utils/urls";

async function verifyEmailIfUnverified(
  userCredential: UserCredential | undefined,
) {
  if (userCredential?.user.emailVerified === false) {
    markPendingEmailVerification(userCredential.user.uid);
    await sendEmailVerification(userCredential.user, {
      url: `${getBaseUrl()}/login`,
    });
  }
}

const useThirdPartyLogin = (): {
  handleGithubLogin: () => void;
  handleGoogleLogin: () => void;
  isLoading: boolean;
  error?: AuthError;
} => {
  const [signInWithGithub, githubUser, githubLoading, githubError] =
    useSignInWithGithub(firebaseAuth);
  const [signInWithGoogle, googleUser, googleLoading, googleError] =
    useSignInWithGoogle(firebaseAuth);

  const error = githubError || googleError;
  const isLoading =
    !!githubUser || githubLoading || !!googleUser || googleLoading;

  const handleGithubLogin = async () => {
    const cred = await signInWithGithub();
    if (cred === undefined) {
      return;
    }
    await verifyEmailIfUnverified(cred);
    if (getAdditionalUserInfo(cred)?.isNewUser) {
      captureProductEvent(productAnalyticsEvents.authSignupCompleted, {
        auth_provider: "github",
      });
      captureLegacyEvent("clicked_create_account", "auth", undefined, {
        accountType: "github",
      });
      if (import.meta.env.PROD) {
        window?.syft?.signup(cred?.user?.email || "", {
          firstName: cred?.user?.displayName || "",
          type: "Github Sign-up",
        });
      }
    } else if (import.meta.env.PROD) {
      //syft
      window?.syft?.identify(cred?.user?.email || "", {
        firstName: cred?.user?.displayName || "",
        type: "Github Login",
      });
    }
    if (import.meta.env.PROD) {
      //commonroom
      window?.signals?.identify({
        email: cred?.user?.email || "",
        name: cred?.user?.displayName || "",
      });
    }
  };

  const handleGoogleLogin = async () => {
    const cred = await signInWithGoogle();
    if (cred === undefined) {
      return;
    }
    await verifyEmailIfUnverified(cred);
    if (getAdditionalUserInfo(cred)?.isNewUser) {
      captureProductEvent(productAnalyticsEvents.authSignupCompleted, {
        auth_provider: "google",
      });
      captureLegacyEvent("clicked_create_account", "auth", undefined, {
        accountType: "google",
      });
      if (import.meta.env.PROD) {
        window?.syft?.signup(cred?.user?.email || "", {
          firstName: cred?.user?.displayName || "",
          type: "Google Sign-up",
        });
      }
    } else if (import.meta.env.PROD) {
      //syft
      window?.syft?.identify(cred?.user?.email || "", {
        firstName: cred?.user?.displayName || "",
        type: "Google Login",
      });
    }

    if (import.meta.env.PROD) {
      //commonroom
      window?.signals?.identify({
        email: cred?.user?.email || "",
        name: cred?.user?.displayName || "",
      });
    }
  };

  return { handleGithubLogin, handleGoogleLogin, isLoading, error };
};

export default useThirdPartyLogin;
